import type { ReactNode, JSX} from "react";
import { cn } from "./cn";

export type ChatBubbleKind =
  | "inbound"
  | "outbound"
  | "email"
  | "email-outbound"
  | "system";

export interface ChatBubbleProps {
  readonly kind: ChatBubbleKind;
  readonly children: ReactNode;
  readonly channelLabel?: ReactNode;
  readonly title?: string;
  readonly time: string;
  readonly delivered?: boolean;
  readonly attachment?: ReactNode;
}

const kindStyles: Readonly<Record<ChatBubbleKind, string>> = {
  inbound: "bg-surface-secondary border-edge",
  outbound: "bg-blue border-transparent text-white",
  email: "bg-email border-transparent text-white",
  "email-outbound": "bg-email border-transparent text-white",
  system: "bg-gradient-to-b from-chat-system-from to-chat-system-to border-transparent self-center text-chat-system-text px-3 py-1.5 rounded-full max-w-[280px] gap-0",
};

/**
 * A chat message bubble supporting multiple kinds (inbound, outbound, email, system).
 * Used in inbox chat and agent chat views.
 */
export function ChatBubble({
  kind,
  children,
  channelLabel,
  title,
  time,
  delivered,
  attachment,
}: ChatBubbleProps): JSX.Element {
  if (kind === "system") {
    return (
      <div className={cn("px-3 py-1.5 rounded-full border flex flex-col", kindStyles[kind])}>
        <div className="flex items-center gap-1.5 text-chat-system-highlight text-[11px] font-medium whitespace-nowrap">
          <span className="flex-1">{children}</span>
          <span className="text-chat-system-muted text-[10px] font-normal ml-auto">{time}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("px-3.5 py-2.5 rounded-lg flex flex-col gap-1 border", kindStyles[kind])}>
      {channelLabel && (
        <div className="text-[11px] opacity-90 flex items-center gap-1">{channelLabel}</div>
      )}
      {title && <div className="font-semibold text-base">{title}</div>}
      <div className="text-base leading-[1.4]">{children}</div>
      {attachment}
      <div className="flex items-center gap-1 justify-start">
        <span className="text-[10px] opacity-70">{time}</span>
        {delivered && <span className="text-xs opacity-70">✓✓</span>}
      </div>
    </div>
  );
}
